import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import type { ApiClientError, ApiSuccess } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';

const NS = 'pricing';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];
const KEY = 'price-simulation-scenarios' as const;

interface UpsScenarioDto {
  scenario_id: string;
  scenario_name: string;
  status: string;
  created_at: string;
  scenario_payload: Record<string, unknown> | null;
}

interface UpsScenariosListMeta {
  total: number;
  page: number;
  per_page: number;
}

const fetchUpsScenarios = async (params: {
  page?: number;
  per_page?: number;
}): Promise<{ data: UpsScenarioDto[]; meta: UpsScenariosListMeta }> => {
  const res = await apiClient.get<ApiSuccess<UpsScenarioDto[], UpsScenariosListMeta>>(
    '/price-simulation-scenarios',
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

const ScenarioDetailDrawer = ({
  scenario,
  open,
  onOpenChange,
}: {
  scenario: UpsScenarioDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation(NS);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {scenario?.scenario_name ??
              t('efs.pricing.ups_scenarios.drawer.title', { defaultValue: 'シナリオ詳細' })}
          </SheetTitle>
        </SheetHeader>
        {scenario ? (
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-efs-text-muted">
                {t('efs.pricing.ups_scenarios.fields.status', { defaultValue: 'ステータス' })}
              </span>
              <Badge variant="outline">{scenario.status}</Badge>
              <span className="text-efs-text-muted">
                {t('efs.pricing.ups_scenarios.fields.created_at', { defaultValue: '作成日時' })}
              </span>
              <span className="font-mono text-xs">
                {new Date(scenario.created_at).toLocaleString()}
              </span>
            </div>
            {scenario.scenario_payload ? (
              <div>
                <p className="mb-2 text-xs font-medium text-efs-text-muted">
                  {t('efs.pricing.ups_scenarios.fields.payload', { defaultValue: 'ペイロード' })}
                </p>
                <pre className="overflow-auto rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-xs leading-relaxed">
                  {JSON.stringify(scenario.scenario_payload, null, 2)}
                </pre>
              </div>
            ) : (
              <p className="text-xs text-efs-text-muted">
                {t('efs.pricing.ups_scenarios.payload_empty', { defaultValue: 'ペイロードなし' })}
              </p>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export const UpsScenariosPage = () => {
  const { t } = useTranslation(NS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [selected, setSelected] = useState<UpsScenarioDto | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const query = useQuery<{ data: UpsScenarioDto[]; meta: UpsScenariosListMeta }, ApiClientError>({
    queryKey: [KEY, 'list', { page, per_page: perPage }],
    queryFn: () => fetchUpsScenarios({ page, per_page: perPage }),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  const columns = useMemo<MasterTableColumn<UpsScenarioDto>[]>(
    () => [
      {
        key: 'scenario_name',
        header: t('efs.pricing.ups_scenarios.fields.scenario_name', { defaultValue: 'シナリオ名' }),
        cell: (row) => row.scenario_name,
      },
      {
        key: 'status',
        header: t('efs.pricing.ups_scenarios.fields.status', { defaultValue: 'ステータス' }),
        cell: (row) => <Badge variant="outline">{row.status}</Badge>,
      },
      {
        key: 'created_at',
        header: t('efs.pricing.ups_scenarios.fields.created_at', { defaultValue: '作成日時' }),
        cell: (row) => (
          <span className="text-xs">{new Date(row.created_at).toLocaleDateString()}</span>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelected(row);
              setDrawerOpen(true);
            }}
          >
            {t('efs.pricing.ups_scenarios.actions.view_detail', { defaultValue: '詳細' })}
          </Button>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.common.nav.ups_scenarios', { defaultValue: 'UPS シナリオ' })}
        subtitle={t('efs.pricing.ups_scenarios.subtitle', {
          defaultValue: '価格シミュレーションシナリオの管理',
        })}
        count={total}
      />
      <TableToolbar count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })} />
      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.scenario_id}
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: (nextPerPage) => {
            setPage(1);
            setPerPage(nextPerPage);
          },
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />

      <ScenarioDetailDrawer scenario={selected} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
};

export default UpsScenariosPage;
